import { Colors, Services, regExp } from '@sigma-frontend-libs/commons'
import { SENDINGS_FALLBACKS_OPTIONS_CLEAN } from '@sigma-legacy-libs/essentials/lib/constants'
import { upgradeArray } from '@sigma-legacy-libs/essentials/lib/utils'
import Actions from '@sigma-legacy-libs/essentials/lib/utils/Actions'

import { components, conditionsTags, getTranslate, icons, moderationColors, services } from '@/utils'

import rulesActionsDialogGeneratorsButtons from '@/components/rules/actions/generators/buttons'
import rulesActionsDialogGeneratorsFallbacks from '@/components/rules/actions/generators/fallbacks'
import rulesActionsDialogGeneratorsModeration from '@/components/rules/actions/generators/moderation'
import rulesActionsDialogGeneratorsText from '@/components/rules/actions/generators/text'
import sendingType from '@/components/rules/commons/type'
import groups from '@/components/trees/group'
import listReadonly from '@/components/trees/list/readonly'
import mccmnc from '@/components/trees/mccmnc'
import groupsReadonly from '@/components/trees/mccmnc/readonly'
import senders from '@/components/trees/sender'

export const baseClass = 'rules'

export const ruleTypes = {
  conditions: 'conditions',
  actions: 'actions'
}

export const ruleTags = upgradeArray(
  Array.from(
    new Set(
      [
        ...conditionsTags,
        ...Actions.tagsOnly
      ]
    )
  )
)

export const ruleTagsByType = {
  [ruleTypes.conditions]: conditionsTags,
  [ruleTypes.actions]: Actions.tagsOnly
}

export const ruleIcons = {
  [ruleTags.text]: icons.text_fields,
  [ruleTags.sender]: icons.person_outline,
  [ruleTags.recipient]: icons.person,
  [ruleTags.group]: icons.group,
  [ruleTags.mccmnc]: icons.public,
  [ruleTags.contactList]: icons.perm_contact_cal,
  [ruleTags.messagePattern]: icons.developer_board,
  [ruleTags.type]: icons.messenger,
  [ruleTags.keywords]: icons.device_hub,
  [ruleTags.yulore]: icons.rule,
  [ruleTags.moderation]: icons.add_moderator,
  [ruleTags.fallbacks]: icons.wrap_text,
  [ruleTags.buttons]: icons.library_add_check,
  [ruleTags.urlShortenerPresetId]: icons.link
}

export const defaultFallback = {
  type: undefined,
  payload: {
    sender: undefined,
    text: undefined
  },
  $options: SENDINGS_FALLBACKS_OPTIONS_CLEAN
}

export const ruleItemMaxTags = {
  [ruleTags.type]: 3,
  [ruleTags.sender]: 1,
  [ruleTags.group]: 1,
  [ruleTags.mccmnc]: 1,
  [ruleTags.yulore]: 3
}

const identityValue = value => {
  return value
}

const sliceByMaxTags = (value, formatOptions) => {
  return value.slice(0, formatOptions.maxTags)
}

const buildCountLabel = (value, formatOptions) => {
  return `${getTranslate(`${baseClass}.labels.${formatOptions.tag}`)} | ${value.length}`
}

const ruleItemValueFormatters = {
  [ruleTypes.conditions]: {
    [ruleTags.text]: identityValue,
    [ruleTags.code]: identityValue,
    [ruleTags.recipient]: identityValue,
    [ruleTags.group]: sliceByMaxTags,
    [ruleTags.sender]: sliceByMaxTags,
    [ruleTags.yulore]: sliceByMaxTags,
    [ruleTags.keywords]: buildCountLabel,
    [ruleTags.contactList]: buildCountLabel,
    [ruleTags.messagePattern]: buildCountLabel,
    [ruleTags.type]: (value, formatOptions) => {
      return value.slice(0, formatOptions.maxTags).map(type => {
        return {
          label: getTranslate(`${Services.sendings}.types.${type}`),
          color: Colors[type]
        }
      })
    }
  },
  [ruleTypes.actions]: {
    [ruleTags.text]: buildCountLabel,
    [ruleTags.fallbacks]: buildCountLabel,
    [ruleTags.buttons]: buildCountLabel,
    [ruleTags.type]: value => {
      return {
        label: getTranslate(`${Services.sendings}.types.${value}`),
        color: Colors[value]
      }
    },
    [ruleTags.sender]: sliceByMaxTags,
    [ruleTags.moderation]: value => {
      return {
        label: getTranslate(`${baseClass}.labels.${value}`),
        color: moderationColors[value]
      }
    }
  }
}

export const resolveRuleItemValue = (ruleType, tag, value, maxTags) => {
  const formattersByType = ruleItemValueFormatters[ruleType] || {}
  const formatter = formattersByType[tag]
  if (formatter) {
    return formatter(value, {
      maxTags,
      tag
    })
  }

  return value
}

export const resolveRuleMainComponent = ({ tag, ruleType, readonly }) => {
  let senderComponent = senders
  let mccmncComponent = mccmnc
  let groupComponent = groups
  let yuloreComponent = components.select
  if (readonly) {
    senderComponent = listReadonly
    mccmncComponent = groupsReadonly
    groupComponent = listReadonly
    yuloreComponent = listReadonly
  }

  const componentMap = {
    [ruleTags.text]: {
      [ruleTypes.actions]: rulesActionsDialogGeneratorsText,
      [ruleTypes.conditions]: components.textarea
    },
    [ruleTags.type]: {
      [ruleTypes.actions]: sendingType({ type: 'string' }),
      [ruleTypes.conditions]: sendingType({ type: 'array' })
    },
    [ruleTags.sender]: senderComponent,
    [ruleTags.fallbacks]: rulesActionsDialogGeneratorsFallbacks,
    [ruleTags.moderation]: rulesActionsDialogGeneratorsModeration,
    [ruleTags.buttons]: rulesActionsDialogGeneratorsButtons,
    [ruleTags.urlShortenerPresetId]: components.select,
    [ruleTags.mccmnc]: mccmncComponent,
    [ruleTags.group]: groupComponent,
    [ruleTags.contactList]: components.select,
    [ruleTags.messagePattern]: components.select,
    [ruleTags.keywords]: components.select,
    [ruleTags.yulore]: yuloreComponent
  }

  const entry = componentMap[tag]
  if (entry && entry[ruleType]) {
    return entry[ruleType]
  }

  if (entry) {
    return entry
  }

  return components['text-field']
}

export const getRuleMainPropsOverrides = tag => {
  if ([ ruleTags.contactList, ruleTags.keywords ].includes(tag)) {
    let service = services.keywordLists
    if (tag === ruleTags.contactList) {
      service = services.contactLists
    }

    return {
      service,
      multiple: true,
      autocomplete: true
    }
  }

  if (tag === ruleTags.messagePattern) {
    return {
      service: services.patterns,
      picks: [ 'id', 'title', 'moderation' ],
      query: { isActive: true },
      multiple: true,
      autocomplete: true
    }
  }

  if (tag === ruleTags.urlShortenerPresetId) {
    return {
      service: Services['url-shortener-settings/presets'],
      autocomplete: true
    }
  }

  if (tag === ruleTags.yulore) {
    return {
      items: [ 1, 5 ],
      multiple: true,
      combobox: true,
      setOnBlur: true,
      autocomplete: true,
      mask: regExp.integer
    }
  }

  return undefined
}

